import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, BookOpen } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { DECLARATIONS, FULL_PRAYER } from "@/lib/prayer";
import { SpokenPrayers } from "@/components/SpokenPrayers";

export const PrayerDeclaration = () => {
  const [i, setI] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % DECLARATIONS.length), 4200);
    return () => clearInterval(t);
  }, []);

  const current = DECLARATIONS[i];

  return (
    <section
      id="prayer"
      data-testid="prayer-declaration-section"
      className="relative bg-ink text-parchment grain overflow-hidden py-20 sm:py-28 border-y border-gold/15"
    >
      <div
        className="absolute inset-0 opacity-80"
        style={{
          background:
            "radial-gradient(circle at 50% 0%, rgba(197,160,89,0.22), transparent 55%), radial-gradient(circle at 85% 90%, rgba(220,38,38,0.10), transparent 45%)",
        }}
      />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-3 mb-8"
        >
          <Flame size={18} className="text-gold-light animate-pulse" />
          <span className="font-mono text-xs uppercase tracking-[0.32em] text-gold-light">
            Prayer &amp; Declaration · In the Name of Yahshua HaMashiach
          </span>
          <Flame size={18} className="text-gold-light animate-pulse" />
        </motion.div>

        <div className="min-h-[220px] sm:min-h-[260px] flex flex-col items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={i}
              data-testid="prayer-rotating-line"
              initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -24, filter: "blur(6px)" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif font-semibold leading-[1.05] tracking-tight text-3xl sm:text-5xl lg:text-6xl max-w-4xl"
            >
              <span className="gold-foil">{current.text}</span>
            </motion.blockquote>
          </AnimatePresence>
          <motion.span
            key={`ref-${i}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-6 font-mono text-xs sm:text-sm uppercase tracking-[0.28em] text-parchment/55"
          >
            {current.ref}
          </motion.span>
        </div>

        <div className="mt-6 flex items-center justify-center gap-2">
          {DECLARATIONS.map((_, idx) => (
            <button
              key={idx}
              aria-label={`Declaration ${idx + 1}`}
              data-testid={`prayer-dot-${idx}`}
              onClick={() => setI(idx)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                idx === i ? "w-8 bg-gold" : "w-1.5 bg-parchment/25 hover:bg-parchment/50"
              }`}
            />
          ))}
        </div>

        <Dialog>
          <DialogTrigger asChild>
            <button
              data-testid="open-full-prayer"
              className="mt-10 group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gold text-ink font-semibold hover:bg-gold-light transition-colors"
            >
              <BookOpen size={18} />
              Pray the Full Declaration
            </button>
          </DialogTrigger>
          <DialogContent
            data-testid="full-prayer-dialog"
            className="max-w-2xl max-h-[85vh] overflow-y-auto"
          >
            <DialogHeader>
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold-deep dark:text-gold-light">
                Apostolic · Pentecostal
              </span>
              <DialogTitle className="font-serif text-2xl sm:text-3xl font-semibold leading-tight text-left">
                {FULL_PRAYER.title}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground text-left pt-1">
                {FULL_PRAYER.subtitle}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-3 space-y-7 text-[15px] leading-relaxed text-foreground/90">
              <p className="font-serif italic text-lg text-gold-deep dark:text-gold-light">
                {FULL_PRAYER.intro}
              </p>

              {FULL_PRAYER.sections.map((s) => (
                <div key={s.heading} className="space-y-3">
                  <h3 className="font-serif text-xl font-semibold flex items-center gap-2">
                    <Flame size={16} className="text-gold-deep dark:text-gold-light" />
                    {s.heading}
                  </h3>
                  {s.paragraphs.map((p, idx) => (
                    <p key={idx}>{p}</p>
                  ))}
                </div>
              ))}

              <div className="space-y-4">
                <h3 className="font-serif text-xl font-semibold">
                  Seven Scriptural Declarations to Speak Daily
                </h3>
                {FULL_PRAYER.scriptures.map((v) => (
                  <div
                    key={v.n}
                    className="pl-4 border-l-2 border-gold/60 space-y-1"
                  >
                    <p className="font-semibold">
                      {v.n}. {v.title}
                    </p>
                    <p className="italic text-foreground/80">{v.verse}</p>
                    <p className="font-mono text-xs text-gold-deep dark:text-gold-light">
                      {v.ref}
                    </p>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <h3 className="font-serif text-xl font-semibold">
                  Final Apostolic Confession
                </h3>
                {FULL_PRAYER.confession.map((c, idx) => (
                  <p key={idx}>{c}</p>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <SpokenPrayers />
      </div>
    </section>
  );
};
